
'use client';

import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useState } from 'react';
import { motion } from 'framer-motion';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { WhiteLogo } from '../white-logo';
import { useToast } from '@/hooks/use-toast';
import { auth } from '@/lib/firebase';
import type { Locale } from '@/i18n-config';
import { cn } from '@/lib/utils';
import { Spinner } from '../ui/spinner';

export function LoginForm({ lang, dict }: { lang: Locale; dict: any }) {
  const { toast, dismiss } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailFormSchema = z.object({
    email: z.string().email({ message: dict.emailInvalid }),
    password: z.string().min(1, { message: dict.passwordRequired }),
  });

  const emailForm = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onEmailSubmit(values: z.infer<typeof emailFormSchema>) {
    dismiss();
    setIsSubmitting(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );
      const idToken = await userCredential.user.getIdToken();

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Use a full page reload to ensure the cookie is set before the next page loads.
        // This is a robust way to prevent race conditions.
        window.location.href = `/${lang}/dashboard`;
      } else {
        throw new Error(result.error || 'Session creation failed on the server.');
      }

    } catch (error: any) {
      console.error('Login Error:', error);
      let errorMessage = dict.invalidCredentials;
      if (error.code === 'auth/invalid-credential') {
        errorMessage = dict.invalidCredentials;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        variant: 'destructive',
        title: dict.loginFailedTitle,
        description: errorMessage,
      });
      setIsSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="w-full max-w-md mx-auto bg-card backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="p-8">
        <div className="flex flex-col items-center justify-center mb-6">
          <WhiteLogo className="w-16 h-16 text-white" />
          <h1 className="text-2xl font-bold text-white mt-4">DiveBalanze</h1>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            {dict.loginTitle}
          </h1>
          <p className="text-muted-foreground mt-2">
            {dict.loginDescription}
          </p>
        </div>

        <div className="mt-6">
          <Form {...emailForm}>
            <form
              onSubmit={emailForm.handleSubmit(onEmailSubmit)}
              className="space-y-4"
            >
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{dict.email}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="m@example.com"
                        {...field}
                        className="h-11"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={emailForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <FormLabel>{dict.password}</FormLabel>
                      <Link
                        href={`/${lang}/forgot-password`}
                        className="ml-auto inline-block text-sm text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {dict.forgotPassword}
                      </Link>
                    </div>
                    <FormControl>
                      <Input
                        type="password"
                        {...field}
                        className="h-11"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <motion.div
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                className="pt-2"
              >
                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Spinner /> : dict.loginButton}
                </Button>
              </motion.div>
            </form>
          </Form>
        </div>

        <div className="mt-8 text-center text-sm">
          <span className="text-muted-foreground">{dict.noAccount} </span>
          <Link
            href={`/${lang}/register`}
            className={cn(
              'font-semibold text-foreground hover:underline',
              isSubmitting && 'pointer-events-none opacity-50'
            )}
          >
            {dict.signUp}
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
